import React, { useState, useRef, useEffect } from 'react';
import { useStore } from '../store/useStore';
import { Avatar, Pill } from './UI';
import { X, Copy, ChevronRight, Package, Check, Edit2, Save, PenLine, Plus, Trash2, ExternalLink, UserCheck, Ban, ShieldCheck } from 'lucide-react';

export const UserProfile = ({ userId, onClose }: { userId: string; onClose: () => void }) => {
  const { users, tickets, operators, activeTicketId, addTagToUser, removeTagFromUser, updateUserNote, blockUser, unblockUser, setActiveTicketId, customerTagCatalog } = useStore();
  const user = users.find(u => u.id === userId);
  const roleLabel = (role?: (typeof operators)[number]['role']) =>
    role === 'OWNER' ? 'Owner' : role === 'ADMIN' ? 'Администратор' : role === 'VIEWER' ? 'Наблюдатель' : 'Оператор';

  const [copiedId, setCopiedId] = useState(false);
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [noteText, setNoteText] = useState(user?.operatorNote || '');
  const [showAddTag, setShowAddTag] = useState(false);
  const [newTagValue, setNewTagValue] = useState('');
  const [toast, setToast] = useState('');
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const tagInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (user) setNoteText(user.operatorNote); }, [user]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2000);
  };

  if (!user) return null;

  const userTickets = tickets.filter(t => t.userId === user.id);

  const handleCopyId = () => {
    navigator.clipboard.writeText(user.telegramId).then(() => {
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 1500);
      showToast('Telegram ID скопирован');
    });
  };

  const handleSaveNote = () => {
    void updateUserNote(user.id, noteText);
    setIsEditingNote(false);
    showToast('Заметка сохранена');
  };

  const handleAddTag = () => {
    const trimmed = newTagValue.trim();
    if (trimmed) {
      void addTagToUser(user.id, trimmed);
      setNewTagValue('');
      setShowAddTag(false);
      showToast(`Тег «${trimmed}» добавлен`);
    }
  };

  const handleRemoveTag = (tag: string) => {
    void removeTagFromUser(user.id, tag);
    showToast(`Тег «${tag}» удалён`);
  };

  const handleOpenTicket = (ticketId: string) => {
    setActiveTicketId(ticketId);
    onClose();
  };

  const handleToggleBlock = () => {
    if (user.isBlocked) {
      void unblockUser(user.id);
      showToast('Пользователь разблокирован');
      return;
    }
    void blockUser(user.id);
    showToast('Пользователь заблокирован');
  };

  const statusLabels: Record<string, string> = {
    pending: 'В обработке', shipped: 'В пути', delivered: 'Доставлен', cancelled: 'Отменён'
  };
  const statusPillType: Record<string, any> = {
    pending: 'medium', shipped: 'pending', delivered: 'open', cancelled: 'closed'
  };
  const ticketStatusLabel: Record<string, string> = { open: 'Открыт', pending: 'В работе', closed: 'Закрыт' };
  const ticketStatusPill: Record<string, any> = { open: 'open', pending: 'pending', closed: 'closed' };

  const tagSuggestions = customerTagCatalog.map((tag) => tag.label);

  return (
    <div className="absolute inset-0 md:static md:w-[320px] bg-[#F5F5F7] border-l border-black/10 flex flex-col h-full z-50 md:z-auto shadow-[-2px_0_12px_rgba(0,0,0,0.05)] md:shadow-none">

      {/* Toast */}
      {toast && (
        <div className="absolute top-14 left-1/2 -translate-x-1/2 z-50 bg-black/80 text-white text-[13px] font-medium px-3 py-1.5 rounded-full backdrop-blur-xl shadow-lg pointer-events-none whitespace-nowrap">
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="h-[60px] px-4 flex items-center justify-between border-b border-black/5 bg-[#F5F5F7]/90 backdrop-blur-2xl shrink-0 sticky top-0 z-10">
        <h3 className="text-[17px] font-semibold text-black tracking-tight">Профиль клиента</h3>
        <button onClick={onClose} className="p-1.5 rounded-full bg-black/5 hover:bg-black/10 text-black/60 transition-colors">
          <X size={20} strokeWidth={2} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-6 mobile-content-safe space-y-5">
        {/* Avatar + Name */}
        <div className="flex flex-col items-center">
          <div className="p-1 bg-white rounded-full shadow-sm mb-3">
            <Avatar name={`${user.firstName} ${user.lastName}`} size="88px" isOnline={user.isOnline} />
          </div>
          <h2 className="text-[22px] font-bold mt-1 leading-tight text-black">{user.firstName} {user.lastName}</h2>
          <span className="text-[15px] text-[#8E8E93] mt-0.5">{user.username}</span>
          <div className="flex items-center gap-1.5 mt-1">
            <span className={`w-2 h-2 rounded-full ${user.isOnline ? 'bg-[#34C759]' : 'bg-[#C7C7CC]'}`} />
            <span className={`text-[13px] ${user.isOnline ? 'text-[#34C759]' : 'text-[#8E8E93]'}`}>
              {user.isOnline ? 'В сети' : 'Оффлайн'}
            </span>
          </div>

          <div className="flex gap-2 mt-5 w-full">
            <button
              onClick={() => { showToast('Переход в чат...'); setTimeout(onClose, 300); }}
              className="flex-1 py-2.5 rounded-[10px] bg-[#007AFF] text-white text-[15px] font-semibold transition-opacity hover:opacity-90 shadow-sm"
            >
              Написать
            </button>
            <button
              onClick={() => { setIsEditingNote(true); setTimeout(() => noteRef.current?.focus(), 100); }}
              className="flex-1 py-2.5 rounded-[10px] bg-black/5 text-black text-[15px] font-semibold transition-colors hover:bg-black/10"
            >
              Заметка
            </button>
          </div>
          <button
            onClick={handleToggleBlock}
            className={`mt-2 w-full py-2.5 rounded-[10px] text-[14px] font-semibold transition-colors inline-flex items-center justify-center gap-2 ${
              user.isBlocked
                ? 'bg-[#34C759]/12 text-[#1A8934] hover:bg-[#34C759]/20'
                : 'bg-[#FF3B30]/12 text-[#C93028] hover:bg-[#FF3B30]/20'
            }`}
          >
            {user.isBlocked ? <ShieldCheck size={16} /> : <Ban size={16} />}
            {user.isBlocked ? 'Разблокировать' : 'Заблокировать'}
          </button>
        </div>

        {/* Info */}
        <div className="bg-white rounded-[12px] overflow-hidden shadow-sm border border-black/5">
          <div className="flex justify-between items-center p-3.5">
            <span className="text-[15px] text-[#8E8E93]">Telegram ID</span>
            <button
              onClick={handleCopyId}
              className="flex items-center gap-1.5 text-black hover:text-[#007AFF] transition-colors"
            >
              <span className="text-[15px] font-medium">{user.telegramId}</span>
              {copiedId ? <Check size={14} className="text-[#34C759]" /> : <Copy size={14} className="opacity-50" />}
            </button>
          </div>
          <div className="h-px bg-black/5 mx-3.5" />
          <div className="flex justify-between items-center p-3.5">
            <span className="text-[15px] text-[#8E8E93]">Первый визит</span>
            <span className="text-[15px] font-medium text-black">{new Date(user.firstSeenAt).toLocaleDateString('ru-RU')}</span>
          </div>
          <div className="h-px bg-black/5 mx-3.5" />
          <div className="flex justify-between items-center p-3.5">
            <span className="text-[15px] text-[#8E8E93]">Тикетов</span>
            <span className="text-[15px] font-medium text-black">{user.totalTickets}</span>
          </div>
          <div className="h-px bg-black/5 mx-3.5" />
          <div className="flex justify-between items-center p-3.5">
            <span className="text-[15px] text-[#8E8E93]">Сообщений</span>
            <span className="text-[15px] font-medium text-black">{user.totalMessages}</span>
          </div>
        </div>

        {/* Assigned operator for active ticket */}
        {(() => {
          const activeTicket = tickets.find(t => t.id === activeTicketId && t.userId === userId);
          if (!activeTicket) return null;
          const assigned = operators.find(o => o.id === activeTicket.assignedTo);
          return (
            <div>
              <h4 className="text-[12px] font-bold text-[#8E8E93] uppercase tracking-widest mb-3 ml-1">Назначенный оператор</h4>
              <div className="bg-white rounded-[12px] border border-black/5 shadow-sm overflow-hidden">
                {assigned ? (
                  <div className="flex items-center gap-3 p-3.5">
                    <div className="w-10 h-10 rounded-full bg-[#007AFF]/10 flex items-center justify-center text-[15px] font-bold text-[#007AFF] shrink-0">
                      {assigned.name.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[15px] font-semibold text-black">{assigned.name}</p>
                      <p className="text-[12px] text-[#8E8E93] flex items-center gap-1.5 mt-0.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${assigned.isOnline ? 'bg-[#34C759]' : 'bg-[#C7C7CC]'}`} />
                        {roleLabel(assigned.role)} · {assigned.isOnline ? 'Онлайн' : 'Офлайн'}
                      </p>
                    </div>
                    <div className="w-8 h-8 rounded-full bg-[#34C759]/15 flex items-center justify-center shrink-0">
                      <UserCheck size={15} className="text-[#1A8934]" strokeWidth={2} />
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-3.5">
                    <div className="w-10 h-10 rounded-full bg-[#F2F2F7] flex items-center justify-center shrink-0">
                      <UserCheck size={18} className="text-[#C7C7CC]" strokeWidth={1.5} />
                    </div>
                    <p className="text-[14px] text-[#8E8E93]">Оператор не назначен</p>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Operator Note */}
        <div>
          <div className="flex justify-between items-center mb-2 ml-1">
            <h4 className="text-[12px] font-bold text-[#8E8E93] uppercase tracking-widest">Заметка оператора</h4>
            {!isEditingNote ? (
              <button
                onClick={() => { setIsEditingNote(true); setTimeout(() => noteRef.current?.focus(), 100); }}
                className="text-[#007AFF] hover:opacity-70 transition-opacity"
              >
                <Edit2 size={14} />
              </button>
            ) : (
              <button onClick={handleSaveNote} className="text-[#34C759] hover:opacity-70 transition-opacity">
                <Save size={14} />
              </button>
            )}
          </div>
          {isEditingNote ? (
            <textarea
              ref={noteRef}
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              onBlur={handleSaveNote}
              onKeyDown={e => { if (e.key === 'Escape') { setIsEditingNote(false); setNoteText(user.operatorNote); } }}
              rows={3}
              placeholder="Добавьте заметку о клиенте..."
              className="w-full bg-white border border-[#007AFF]/40 rounded-[10px] p-3 text-[14px] text-black outline-none resize-none shadow-sm focus:ring-2 focus:ring-[#007AFF]/20 transition-all placeholder:text-[#C7C7CC]"
            />
          ) : (
            <button
              onClick={() => { setIsEditingNote(true); setTimeout(() => noteRef.current?.focus(), 100); }}
              className="w-full text-left bg-white rounded-[10px] p-3 shadow-sm border border-black/5 hover:border-black/10 transition-colors"
            >
              {user.operatorNote ? (
                <p className="text-[14px] text-black leading-relaxed">{user.operatorNote}</p>
              ) : (
                <p className="text-[14px] text-[#C7C7CC] italic">Нажмите, чтобы добавить заметку...</p>
              )}
            </button>
          )}
        </div>

        {/* Tags */}
        <div>
          <div className="flex justify-between items-center mb-3 ml-1">
            <h4 className="text-[12px] font-bold text-[#8E8E93] uppercase tracking-widest">Теги</h4>
          </div>
          <div className="flex flex-wrap gap-2">
            {user.tags.map(tag => (
              <div key={tag} className="flex items-center gap-1 bg-white border border-black/5 shadow-sm px-3 py-1.5 rounded-[8px] group">
                <span className="text-[13px] font-medium text-black">{tag}</span>
                <button onClick={() => handleRemoveTag(tag)} className="text-[#C7C7CC] hover:text-[#FF3B30] transition-colors">
                  <X size={13} strokeWidth={2.5} />
                </button>
              </div>
            ))}

            {showAddTag ? (
              <div className="flex items-center bg-white border border-[#007AFF]/40 rounded-[8px] px-2 py-1 shadow-sm gap-1">
                <input
                  ref={tagInputRef}
                  value={newTagValue}
                  onChange={e => setNewTagValue(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleAddTag(); if (e.key === 'Escape') { setShowAddTag(false); setNewTagValue(''); } }}
                  placeholder="Тег..."
                  className="text-[13px] outline-none w-24 bg-transparent placeholder:text-[#C7C7CC]"
                  autoFocus
                />
                <button onClick={handleAddTag} className="text-[#34C759] hover:opacity-70">
                  <Check size={14} strokeWidth={2.5} />
                </button>
                <button onClick={() => { setShowAddTag(false); setNewTagValue(''); }} className="text-[#C7C7CC] hover:text-[#FF3B30]">
                  <X size={14} strokeWidth={2.5} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowAddTag(true)}
                className="text-[13px] text-[#007AFF] font-medium px-3 py-1.5 rounded-[8px] border border-dashed border-[#007AFF]/30 hover:bg-[#007AFF]/5 transition-colors"
              >
                + Добавить
              </button>
            )}
          </div>

          {/* Tag suggestions */}
          {showAddTag && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {tagSuggestions.filter(s => !user.tags.includes(s) && (!newTagValue || s.toLowerCase().includes(newTagValue.toLowerCase()))).slice(0, 5).map(s => (
                <button
                  key={s}
                  onClick={() => { void addTagToUser(user.id, s); setShowAddTag(false); setNewTagValue(''); showToast(`Тег «${s}» добавлен`); }}
                  className="text-[12px] px-2 py-1 rounded-full bg-[#007AFF]/8 text-[#007AFF] hover:bg-[#007AFF]/15 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Orders */}
        <div>
          <div className="flex justify-between items-center mb-3 ml-1">
            <h4 className="text-[12px] font-bold text-[#8E8E93] uppercase tracking-widest">Заказы</h4>
            <button
              onClick={() => showToast('Прикрепление заказов в разработке')}
              className="text-[13px] text-[#007AFF] font-medium hover:underline"
            >
              + Прикрепить
            </button>
          </div>
          <div className="space-y-2">
            {user.linkedOrders.length === 0 ? (
              <div className="text-[14px] text-[#8E8E93] text-center p-4 bg-white rounded-[12px] border border-dashed border-black/10">
                Нет прикреплённых заказов
              </div>
            ) : (
              user.linkedOrders.map(order => (
                <div
                  key={order.id}
                  className="bg-white border border-black/5 rounded-[12px] p-3.5 shadow-sm flex flex-col gap-2 hover:border-black/15 transition-colors cursor-pointer group"
                  onClick={() => showToast(`Заказ ${order.orderNumber}`)}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-[#F2F2F7] rounded-md group-hover:bg-[#007AFF]/10 transition-colors">
                        <Package size={16} className="text-[#8E8E93] group-hover:text-[#007AFF] transition-colors" />
                      </div>
                      <span className="font-semibold text-[15px] text-black">{order.orderNumber}</span>
                    </div>
                    <Pill type={statusPillType[order.status] || 'tag'}>
                      {statusLabels[order.status] || order.status}
                    </Pill>
                  </div>
                  <div className="flex justify-between text-[13px] text-[#8E8E93] ml-8">
                    <span>{new Date(order.date).toLocaleDateString('ru-RU')}</span>
                    <span className="font-medium text-black">{order.amount.toLocaleString('ru-RU')} ₽</span>
                  </div>
                  {order.items.length > 0 && (
                    <div className="ml-8 text-[12px] text-[#8E8E93] truncate">
                      {order.items.map(item => `${item.name} ×${item.qty}`).join(', ')}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Ticket History */}
        <div>
          <h4 className="text-[12px] font-bold text-[#8E8E93] uppercase tracking-widest mb-3 ml-1">История тикетов</h4>
          <div className="bg-white border border-black/5 shadow-sm rounded-[12px] overflow-hidden">
            {userTickets.length === 0 ? (
              <div className="p-4 text-center text-[14px] text-[#8E8E93]">Нет тикетов</div>
            ) : (
              userTickets.slice(0, 5).map((t, i) => (
                <button
                  key={t.id}
                  onClick={() => handleOpenTicket(t.id)}
                  className={`w-full p-3 flex items-center justify-between text-left hover:bg-[#F2F2F7] transition-colors ${i !== Math.min(userTickets.length, 5) - 1 ? 'border-b border-black/5' : ''}`}
                >
                  <div className="flex-1 min-w-0 pr-3">
                    <div className="text-[14px] font-medium truncate text-black">{t.subject}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[12px] text-[#8E8E93]">{new Date(t.createdAt).toLocaleDateString('ru-RU')}</span>
                      <Pill type={ticketStatusPill[t.status]}>{ticketStatusLabel[t.status]}</Pill>
                    </div>
                  </div>
                  <ExternalLink size={16} className="text-[#007AFF] shrink-0" />
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
